/**
 * Shared File Helper
 * 
 * Utilities for retrieving files shared via the Web Share Target API.
 * Files are stored in Cache API by the service worker and can be retrieved here.
 */

export interface SharedFileMetadata {
  name: string
  size: number
  type: string
  timestamp: number
  cacheKey: string
}

/**
 * Check if there are any shared files waiting to be processed
 * @param timestamp Optional timestamp from URL parameter
 * @returns Metadata if shared file exists, null otherwise
 */
export async function getSharedFileMetadata(
  timestamp?: string | number
): Promise<SharedFileMetadata | null> {
  try {
    const ts = timestamp ? parseInt(timestamp.toString()) : await getLatestSharedTimestamp()
    if (!ts) return null

    // Try to get from IndexedDB first
    const metadata = await getMetadataFromIndexedDB(ts)
    if (metadata) return metadata

    // Fallback: try to get from cache headers
    const cache = await caches.open('shared-files')
    const cacheKey = `/shared-file-${ts}`
    const response = await cache.match(cacheKey)
    
    if (response) {
      return {
        name: response.headers.get('X-File-Name') || 'shared-file',
        size: parseInt(response.headers.get('X-File-Size') || '0'),
        type: response.headers.get('Content-Type') || 'application/octet-stream',
        timestamp: ts,
        cacheKey,
      }
    }

    return null
  } catch (error) {
    console.error('Error getting shared file metadata:', error)
    return null
  }
}

/**
 * Retrieve the actual File object from cache
 * @param metadata Metadata from getSharedFileMetadata
 * @returns File object or null
 */
export async function getSharedFile(
  metadata: SharedFileMetadata
): Promise<File | null> {
  try {
    const cache = await caches.open('shared-files')
    const response = await cache.match(metadata.cacheKey)
    
    if (!response) return null

    const blob = await response.blob()
    const file = new File([blob], metadata.name, {
      type: metadata.type,
      lastModified: metadata.timestamp,
    })

    return file
  } catch (error) {
    console.error('Error retrieving shared file:', error)
    return null
  }
}

/**
 * Delete a shared file from cache after it's been processed
 * @param metadata Metadata of file to delete
 */
export async function deleteSharedFile(metadata: SharedFileMetadata): Promise<void> {
  try {
    const cache = await caches.open('shared-files')
    await cache.delete(metadata.cacheKey)
    
    // Also delete from IndexedDB
    await deleteMetadataFromIndexedDB(metadata.timestamp)
  } catch (error) {
    console.error('Error deleting shared file:', error)
  }
}

/**
 * Get the latest shared file timestamp
 */
async function getLatestSharedTimestamp(): Promise<number | null> {
  try {
    const cache = await caches.open('shared-files')
    const keys = await cache.keys()
    
    let latestTimestamp = 0
    for (const request of keys) {
      const response = await cache.match(request)
      if (response) {
        const timestamp = parseInt(response.headers.get('X-Shared-Timestamp') || '0')
        if (timestamp > latestTimestamp) {
          latestTimestamp = timestamp
        }
      }
    }
    
    return latestTimestamp > 0 ? latestTimestamp : null
  } catch (error) {
    console.error('Error getting latest shared timestamp:', error)
    return null
  }
}

/**
 * Get metadata from IndexedDB
 */
async function getMetadataFromIndexedDB(
  timestamp: number
): Promise<SharedFileMetadata | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open('gearlens-shared-files', 1)
      
      request.onerror = () => resolve(null)
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readonly')
        const store = transaction.objectStore('files')
        const getRequest = store.get(timestamp)
        
        getRequest.onsuccess = () => {
          db.close()
          resolve(getRequest.result || null)
        }
        getRequest.onerror = () => {
          db.close()
          resolve(null)
        }
      }
    } catch (error) {
      console.error('Error accessing IndexedDB:', error)
      resolve(null)
    }
  })
}

/**
 * Delete metadata from IndexedDB
 */
async function deleteMetadataFromIndexedDB(timestamp: number): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open('gearlens-shared-files', 1)
      
      request.onerror = () => resolve() // Fail silently
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readwrite')
        const store = transaction.objectStore('files')
        
        store.delete(timestamp)
        
        transaction.oncomplete = () => {
          db.close()
          resolve()
        }
        transaction.onerror = () => {
          db.close()
          resolve() // Fail silently
        }
      }
    } catch (error) {
      console.error('Error deleting from IndexedDB:', error)
      resolve() // Fail silently
    }
  })
}
